import { useTranslation } from 'react-i18next';
import type {
  IntegrationState,
  InvitationStatus,
  OnboardingState,
  ProductState,
  Severity,
} from '@/types/platform';
import styles from './platform-ui.module.css';

/**
 * One badge for every state vocabulary in the platform console.
 *
 * The tone is the point. An administrator scanning a list of two hundred
 * institutions reads colour before words, so the mapping below is about who is
 * blocked and how badly, not about which enum the value came from:
 * `critical` means someone has lost access or a case has died, `warning` means
 * CSKY owes work, `progress` means work is under way, `positive` means done.
 */

export type BadgeTone = 'neutral' | 'info' | 'progress' | 'positive' | 'warning' | 'critical';

const ONBOARDING_TONES: Record<OnboardingState, BadgeTone> = {
  DRAFT: 'neutral',
  INFORMATION_REQUIRED: 'warning',
  DOCUMENTS_PENDING: 'warning',
  UNDER_REVIEW: 'info',
  COMPLIANCE_REVIEW: 'info',
  TECHNICAL_CONFIGURATION: 'progress',
  PRODUCT_CONFIGURATION: 'progress',
  ADMIN_INVITATION_PENDING: 'warning',
  READY_FOR_ACTIVATION: 'info',
  ACTIVE: 'positive',
  REJECTED: 'critical',
  SUSPENDED: 'critical',
  CLOSED: 'neutral',
};

const PRODUCT_TONES: Record<ProductState, BadgeTone> = {
  NOT_SUBSCRIBED: 'neutral',
  PENDING_CONFIGURATION: 'warning',
  CONFIGURING: 'progress',
  ENABLED: 'positive',
  SUSPENDED: 'warning',
  DISABLED: 'critical',
  EXPIRED: 'critical',
};

const INTEGRATION_TONES: Record<IntegrationState, BadgeTone> = {
  NOT_CONFIGURED: 'neutral',
  CONFIGURING: 'progress',
  HEALTHY: 'positive',
  DEGRADED: 'warning',
  FAILED: 'critical',
  DISABLED: 'neutral',
};

const INVITATION_TONES: Record<InvitationStatus, BadgeTone> = {
  PENDING: 'info',
  ACCEPTED: 'positive',
  EXPIRED: 'warning',
  REVOKED: 'neutral',
  CANCELLED: 'neutral',
  FAILED: 'critical',
};

const SEVERITY_TONES: Record<Severity, BadgeTone> = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'critical',
};

/**
 * The DRAFT → READY_FOR_REVIEW → APPROVED → ACTIVE → SUPERSEDED lifecycle the
 * configuration-governance modules share (grading strategies use the three-state
 * subset). SUPERSEDED is neutral, not critical: a superseded version is history
 * doing its job, and painting it red would make every archive look like an
 * incident list.
 */
const LIFECYCLE_TONES: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  READY_FOR_REVIEW: 'info',
  APPROVED: 'progress',
  ACTIVE: 'positive',
  SUPERSEDED: 'neutral',
};

/**
 * Verification credential lifecycle (identity service CredentialStatus).
 * REPLACED is neutral like SUPERSEDED above — a replaced card is the replace
 * workflow having worked, not an incident; the card that must read as danger at
 * a gate is the SUSPENDED or REVOKED one.
 */
const CREDENTIAL_TONES: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  SUSPENDED: 'warning',
  REVOKED: 'critical',
  EXPIRED: 'critical',
  REPLACED: 'neutral',
};

type BadgeKind =
  | 'onboarding'
  | 'product'
  | 'integration'
  | 'invitation'
  | 'severity'
  | 'lifecycle'
  | 'credential';

const TONE_LOOKUP: Record<BadgeKind, Record<string, BadgeTone>> = {
  onboarding: ONBOARDING_TONES,
  product: PRODUCT_TONES,
  integration: INTEGRATION_TONES,
  invitation: INVITATION_TONES,
  severity: SEVERITY_TONES,
  lifecycle: LIFECYCLE_TONES,
  credential: CREDENTIAL_TONES,
};

interface StateBadgeProps {
  kind: BadgeKind;
  /** Typed loosely because the value crosses a network boundary; an unknown
      state renders its raw code rather than crashing or vanishing. */
  value: string;
  size?: 'sm' | 'md';
}

export function StateBadge({ kind, value, size = 'md' }: StateBadgeProps) {
  const { t } = useTranslation();
  const tone = TONE_LOOKUP[kind][value] ?? 'neutral';
  const key = `platform.state.${kind}.${value}`;
  const label = t(key);

  return (
    <span
      className={`${styles.badge} ${styles[`badge_${tone}`]} ${size === 'sm' ? styles.badgeSm : ''}`}
      data-state={value}
    >
      {/* A state the server invented after this build shipped still has to be
          readable, so an unresolved key falls back to the raw code. */}
      {label === key ? value : label}
    </span>
  );
}

export function toneForSeverity(severity: Severity): BadgeTone {
  return SEVERITY_TONES[severity] ?? 'info';
}
